/**
 * Declare what a list filters on; get the filtered rows and the panel back.
 *
 * Every filtered screen was writing the same three things by hand — a piece of
 * state per control, a predicate chain, and a grid of fields — so each one
 * decided the layout, the clear behaviour and the result count again, and they
 * disagreed. A screen names its filters here and wires the result straight
 * into {@link CollectionView}.
 *
 * @module components/ui/useCollectionFilters
 */

import { type ReactNode, useCallback, useId } from "react";

import type { CollectionFiltering } from "./CollectionView";
import { FormField, SelectField, TextField } from "./Form";
import { pluralNoun } from "./plural";
import { useStoredState } from "./useStoredState";

/**
 * The value meaning "do not narrow on this", and the option that heads a
 * select. Deliberately not `"all"`: that is a plausible option value — an
 * "All-year" intake, a discipline literally called All — and a select whose
 * own option collided with the sentinel could never narrow anything.
 */
const ALL = "__all__";

export interface FilterOption {
  value: string;
  label: string;
}

interface FilterDefBase {
  /** Identifies the filter's own state; also the control's id. */
  key: string;
  /** Names the field. Never the panel's own heading repeated. */
  label: string;
  help?: string;
  "data-testid"?: string;
}

export interface TextFilterDef<Row> extends FilterDefBase {
  kind: "text";
  placeholder?: string;
  /** Whether a row survives. `query` arrives trimmed and lowercased. */
  match: (row: Row, query: string) => boolean;
}

export interface SelectFilterDef<Row> extends FilterDefBase {
  kind: "select";
  /** Heads the list, and is what "not filtering on this" reads as. */
  allLabel: string;
  /** A fixed set, or one derived from the rows before any filter narrows them. */
  options: readonly FilterOption[] | ((rows: readonly Row[]) => readonly FilterOption[]);
  match: (row: Row, value: string) => boolean;
}

export type FilterDef<Row> = TextFilterDef<Row> | SelectFilterDef<Row>;

export interface CollectionFiltersOptions {
  /** Names and heads the panel, e.g. `Filter programmes`. */
  label: string;
  /** Names the visible result count, for a screen whose spec already reads it. */
  countTestId?: string;
  /** The collection's noun, singular: `programme`, `revision`. The count
   *  decides which form is rendered. */
  noun: string;
  defaultOpen?: boolean;
  /** Remembers the choices across visits; shares the list's own key. */
  storageKey?: string;
  "data-testid"?: string;
}

export interface CollectionFiltersResult<Row> {
  /** The rows that survived every filter. */
  rows: readonly Row[];
  /** Plugs straight into `CollectionView`'s `filtering` prop. */
  filtering: CollectionFiltering;
}

function readValues(stored: unknown): Readonly<Record<string, string>> | undefined {
  if (typeof stored !== "object" || stored === null || Array.isArray(stored)) {
    return undefined;
  }
  return Object.fromEntries(
    Object.entries(stored).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );
}

function emptyValue<Row>(def: FilterDef<Row>): string {
  return def.kind === "select" ? ALL : "";
}

function keeps<Row>(def: FilterDef<Row>, value: string, row: Row): boolean {
  if (def.kind === "select") {
    return value === ALL || def.match(row, value);
  }
  const query = value.trim().toLowerCase();
  return query === "" || def.match(row, query);
}

function optionsFor<Row>(def: FilterDef<Row>, rows: readonly Row[]): readonly FilterOption[] {
  if (def.kind !== "select") {
    return [];
  }
  return typeof def.options === "function" ? def.options(rows) : def.options;
}

/**
 * How a filter panel's fields sit inside it.
 *
 * Two columns where there is room, one where there is not, and every control
 * filling its track. A row of fields narrower than the panel they sit in is
 * the defect this replaces. Exported because `FilterBar` drops its children
 * into a padded box and lays nothing out, so a screen that builds its own
 * `CollectionFiltering` — because it holds the values itself rather than
 * letting this hook hold them — needs the same row.
 */
export const FILTER_CONTROL_ROW = "grid gap-3 lg:grid-cols-2 lg:items-end";

/**
 * @example
 * ```tsx
 * const { rows, filtering } = useCollectionFilters(programmes, [
 *   { kind: "select", key: "state", label: "State", allLabel: "All states",
 *     options: STATES, match: (p, v) => p.workflowState === v },
 *   { kind: "text", key: "search", label: "Search",
 *     match: (p, q) => p.title.toLowerCase().includes(q) },
 * ], { label: "Filter programmes", noun: "programme" });
 * ```
 */
export function useCollectionFilters<Row>(
  rows: readonly Row[],
  defs: readonly FilterDef<Row>[],
  {
    label,
    noun,
    countTestId,
    defaultOpen = undefined,
    storageKey = undefined,
    "data-testid": testId,
  }: CollectionFiltersOptions,
): CollectionFiltersResult<Row> {
  const [values, setValues] = useStoredState<Readonly<Record<string, string>>>(
    storageKey === undefined ? undefined : `sqFilters_${storageKey}`,
    {},
    readValues,
  );
  // Namespaced, because a control id is also its label's `for` target: two
  // collections on one screen sharing a filter key gave two controls the same
  // id, so the label focused the wrong one and `getByLabelText` threw.
  const scope = useId();

  // Once per render: `valueOf` runs for every row, and a derived list read
  // there rebuilt the options for each one.
  const optionsOf = new Map(defs.map((def) => [def.key, optionsFor(def, rows)]));

  const valueOf = (def: FilterDef<Row>): string => {
    const raw = values[def.key];
    if (raw === undefined) {
      return emptyValue(def);
    }
    if (
      def.kind === "select" &&
      raw !== ALL &&
      !optionsOf.get(def.key)?.some((opt) => opt.value === raw)
    ) {
      return ALL;
    }
    return raw;
  };

  // Forgotten, not only read past: kept, a choice the rows stopped offering
  // came back into force when they offered it again, under a select that had
  // read "All" in between. Set during render, so the stale value never paints.
  const stale = defs.filter(
    (def) => values[def.key] !== undefined && valueOf(def) === ALL && values[def.key] !== ALL,
  );
  if (stale.length > 0) {
    const keys = new Set(stale.map((def) => def.key));
    setValues((current) =>
      Object.fromEntries(Object.entries(current).filter(([key]) => !keys.has(key))),
    );
  }

  const clear = useCallback(() => {
    setValues({});
  }, [setValues]);

  const filtered = rows.filter((row) => defs.every((def) => keeps(def, valueOf(def), row)));

  // From the controls, not from the count: a filter that happens to exclude
  // nothing is still set, and keying off the result count hides the clear
  // control while the select visibly still reads "Draft".
  const active = defs.some((def) => valueOf(def) !== emptyValue(def));

  const controls: ReactNode = (
    <div className="space-y-2">
      <div className={FILTER_CONTROL_ROW}>
        {defs.map((def) => {
          const id = `${scope}${def.key}`;
          const controlTestId = def["data-testid"] ?? def.key;
          const set = (next: string): void => {
            setValues((current) => ({ ...current, [def.key]: next }));
          };
          return (
            <FormField key={def.key} label={def.label} htmlFor={id} helpText={def.help}>
              {def.kind === "select" ? (
                <SelectField
                  id={id}
                  value={valueOf(def)}
                  onChange={set}
                  options={[{ value: ALL, label: def.allLabel }, ...(optionsOf.get(def.key) ?? [])]}
                  data-testid={controlTestId}
                />
              ) : (
                <TextField
                  id={id}
                  value={valueOf(def)}
                  onChange={set}
                  placeholder={def.placeholder}
                  data-testid={controlTestId}
                />
              )}
            </FormField>
          );
        })}
      </div>
    </div>
  );

  // The one result count in the screen, and it rides on the panel's summary so
  // collapsing the panel cannot take it away. A second beside the list would
  // restate it in different words and read as a contradiction.
  const count = `${filtered.length} of ${rows.length} ${pluralNoun(rows.length, noun)} shown`;

  return {
    rows: filtered,
    filtering: {
      label,
      controls,
      count,
      countTestId,
      active,
      onClear: clear,
      defaultOpen,
      "data-testid": testId,
    },
  };
}
